import React, { useCallback, useEffect, useState } from "react";
import { Bot, Star, Utensils, ShoppingBag } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, EmptyState, Toast, Input } from "@/ui";

export default function Assistants() {
  const [data, setData] = useState(null);      // {templates, defaults}
  const [deck, setDeck] = useState(null);       // shops (for capture source)
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState("food");
  const [srcShop, setSrcShop] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, o] = await Promise.all([api.get("/deck/templates"), api.get("/deck/overview")]);
      setData(t.data);
      setDeck(o.data);
    } catch (e) { setError(friendlyError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };
  const isOwner = deck?.role === "owner";
  const doneShops = (deck?.shops || []).filter((s) => s.setupStatus === "done");

  const capture = async () => {
    if (!srcShop) { setError("Pick a fully set-up shop to capture from."); return; }
    setSaving(true);
    try { await api.post("/deck/templates/capture", { shopId: srcShop, name, kind }); setName(""); flash("assistant captured"); load(); }
    catch (e) { setError(friendlyError(e)); } finally { setSaving(false); }
  };
  const setDefault = async (k, templateId) => {
    try { await api.post("/deck/templates/default", { kind: k, templateId: templateId || null }); flash("default set"); load(); }
    catch (e) { setError(friendlyError(e)); }
  };

  const nameFor = (id) => (data?.templates || []).find((t) => t.id === id)?.name || "None yet";

  return (
    <Layout>
      <h1 className="t-h1">Ready-made assistants</h1>
      <p className="muted t-small mb-5">Capture a fully set-up shop's assistant, name it, and pick the default for Food and Retail. New shops get the right one automatically.</p>

      {error ? <Toast tone="bad" data-testid="assistants-error">{error}</Toast> : null}

      {!data ? <Skeleton style={{ height: 120 }} /> : (
        <>
          <div className="grid grid-2 mb-5" style={{ gap: 12 }}>
            {[["food", "Food", Utensils], ["retail", "Retail", ShoppingBag]].map(([k, label, Icon]) => (
              <Card className="p-4" key={k} data-testid={`assistant-default-${k}`}>
                <div className="flex items-center gap-2 text-sm font-semibold"><Icon size={16} strokeWidth={1.75} />{label} default</div>
                <div className="text-sm mt-1" style={{ color: "#475569" }} data-testid={`default-name-${k}`}>{nameFor(data.defaults?.[k])}</div>
                {isOwner ? (
                  <select className="select mt-2" style={{ width: "100%" }} value={data.defaults?.[k] || ""} data-testid={`default-select-${k}`}
                    onChange={(e) => setDefault(k, e.target.value)}>
                    <option value="">None</option>
                    {(data.templates || []).filter((t) => t.kind === k).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                ) : null}
              </Card>
            ))}
          </div>

          {isOwner ? (
            <Card className="p-4 mb-5" data-testid="capture-card">
              <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Star size={16} strokeWidth={1.75} />Capture a new one</div>
              <div className="flex gap-2 flex-wrap items-center">
                <select className="select" value={srcShop} data-testid="capture-shop" onChange={(e) => setSrcShop(e.target.value)} style={{ minWidth: 180 }}>
                  <option value="">Pick a set-up shop…</option>
                  {doneShops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select className="select" value={kind} data-testid="capture-kind" onChange={(e) => setKind(e.target.value)}>
                  <option value="food">Food</option>
                  <option value="retail">Retail</option>
                </select>
                <Input placeholder="Name it (optional)" value={name} data-testid="capture-name" onChange={(e) => setName(e.target.value)} style={{ minWidth: 180 }} />
                <Button variant="primary" data-testid="capture-save" loading={saving} onClick={capture}>capture</Button>
              </div>
            </Card>
          ) : null}

          {data.templates?.length ? (
            <div className="stack" style={{ gap: 10 }} data-testid="templates-list">
              {data.templates.map((t) => (
                <Card className="p-3 flex items-center justify-between" key={t.id} data-testid={`template-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <Bot size={18} strokeWidth={1.75} />
                    <div><div className="text-sm font-semibold">{t.name}</div><div className="text-xs" style={{ color: "var(--text-3)" }}>from {t.sourceShopName}</div></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Chip tone="info">{t.kind}</Chip>
                    {data.defaults?.[t.kind] === t.id ? <Chip tone="good" data-testid={`template-default-${t.id}`}>default</Chip> : null}
                  </div>
                </Card>
              ))}
            </div>
          ) : <EmptyState icon={Bot} title="No ready-made assistants yet" body="Capture one from a fully set-up shop to reuse it." />}
        </>
      )}

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="assistants-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
